/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { Text, Grid, Flex, TextField, Select, Button, TextArea, Heading, Avatar, Card, Badge, Separator, Dialog, RadioGroup } from "@radix-ui/themes";
import DropZone from "./DropZone";
import { getAllCategoryData, updateCampaignDetails } from "../Services/DashboardApi";
import { GeneralContextFunc } from "../Context/GeneralContex";
import trashIcon from "../assets/trashIcon.png";
import { handleDelete, handleUpload } from "../Services/S3bucket";
import { useLocation, useNavigate } from "react-router-dom";
import YoutubeThumbnail from "./YoutubeThumbnail";

const CampaignDetails = ({ campaignFee, campaignGoals, profileAssets, getCampaignDataById }) => {
  const navigate = useNavigate();
  const { setloader, initAlert } = GeneralContextFunc();
  const location = useLocation();
  const [CampaignFee, setCampaignFee] = useState({
    shippingAndDelivery: {
      name: "shippingAndDelivery",
      amount: 0,
      description: "Includes packaging and direct delivery to recipient(s)"
    },
    fulfillmentSupport: {
      name: "fulfillmentSupport",
      amount: 0,
      description: "Covers labor, packaging materials, inserts and coordination"
    },
    taxesAndProcessingFees: {
      name: "taxesAndProcessingFees",
      amount: 0,
      description: "Includes sales tax, credit card/payment processing and compliance costs"
    },
    raflPlatformSupport: {
      name: "raflPlatformSupport",
      amount: 0,
      description: "Helps cover platform costs and keep RAFL running transparently & independently"
    },
    emergencyBuffer: {
      name: "emergencyBuffer",
      amount: 0,
      description: "Covers unexpected issues like reshipments or vendor delays"
    }
  });
  const [CampaignGoals, setCampaignGoals] = useState({
    title: "",
    toFunds: "",
    blog: "",
    category: "",
    subCategory: "",
    country: "",
    donatingCount: "",
    fundsFor: "",
    itemCount: "",
    story: "",
    zipCode: "",
    city: "",
    state: "",
    userDetails: [],
    goals: [],
    approxCost: "",
  });
  const [ProfileAssets, setProfileAssets] = useState({
    coverImage: "",
    logo: "",
    youtubeLink: "",
    additionalPhotos: [],
    story: "",
  });

  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [newCustomField, setNewCustomField] = useState({
    name: "",
    description: "",
    value: "",
  });

  const [gValue, setGValue] = useState(false);

  const [categList, setCategList] = useState([]);
  const [subCategList, setSubCategList] = useState(null);

  const totalGoalValue = useMemo(() => {
    let customFieldsTotal = 0;

    // Calculate custom fields contribution (all flat values)
    customFields.forEach(field => {
      customFieldsTotal += Number(field.value) || 0;
    });

    // Calculate standard fee fields
    const standardFeesTotal = Number(CampaignFee.shippingAndDelivery.amount) + 
                             Number(CampaignFee.fulfillmentSupport.amount) + 
                             Number(CampaignFee.taxesAndProcessingFees.amount) + 
                             Number(CampaignFee.raflPlatformSupport.amount) + 
                             Number(CampaignFee.emergencyBuffer.amount);

    // Add moneyNeeded from API response if available
    const moneyNeeded = Number(campaignFee?.moneyNeeded) || 0;

    return standardFeesTotal + customFieldsTotal + moneyNeeded;
  }, [CampaignFee.shippingAndDelivery.amount, CampaignFee.fulfillmentSupport.amount, CampaignFee.taxesAndProcessingFees.amount, CampaignFee.raflPlatformSupport.amount, CampaignFee.emergencyBuffer.amount, customFields, campaignFee?.moneyNeeded]);

  // Custom field functions
  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      initAlert("Please enter a field name", "warning");
      return;
    }
    if (!newCustomField.value || isNaN(Number(newCustomField.value))) {
      initAlert("Please enter a valid value", "warning");
      return;
    }

    setCustomFields([...customFields, { ...newCustomField, id: Date.now() }]);
    setNewCustomField({ name: "", description: "", value: "" });
    setShowCustomFieldModal(false);
    setGValue(true);
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(field => field.id !== id));
    setGValue(true);
  };

  const updateCustomField = (id, field, value) => {
    setCustomFields(customFields.map(f =>
      f.id === id ? { ...f, [field]: value } : f
    ));
    setGValue(true);
  };

  const [editingField, setEditingField] = useState(null);
  const [editFieldData, setEditFieldData] = useState({
    name: "",
    description: "",
    value: ""
  });

  const [editingStandardField, setEditingStandardField] = useState(null);
  const [editStandardFieldData, setEditStandardFieldData] = useState({
    name: "",
    description: "",
    amount: 0
  });

  const startEditingField = (field) => {
    setEditingField(field.id);
    setEditFieldData({
      name: field.name,
      description: field.description || "",
      value: field.value
    });
  };

  const saveEditField = () => {
    if (!editFieldData.name.trim()) {
      initAlert("Please enter a field name", "warning");
      return;
    }
    if (!editFieldData.value || isNaN(Number(editFieldData.value))) {
      initAlert("Please enter a valid value", "warning");
      return;
    }

    setCustomFields(customFields.map(f =>
      f.id === editingField ? { ...f, ...editFieldData } : f
    ));
    setEditingField(null);
    setEditFieldData({ name: "", description: "", value: "" });
    setGValue(true);
  };

  const cancelEditField = () => {
    setEditingField(null);
    setEditFieldData({ name: "", description: "", value: "" });
  };

  const startEditingStandardField = (fieldName) => {
    // Create readable display names
    const getDisplayName = (key) => {
      const displayNames = {
        shippingAndDelivery: "Shipping & Delivery",
        fulfillmentSupport: "Fulfillment Support",
        taxesAndProcessingFees: "Taxes & Processing Fees",
        raflPlatformSupport: "RAFL Platform Support",
        emergencyBuffer: "Emergency Buffer"
      };
      return displayNames[key] || key;
    };

    setEditingStandardField(fieldName);
    setEditStandardFieldData({
      name: getDisplayName(fieldName), // Use display name
      description: CampaignFee[fieldName].description || "",
      amount: CampaignFee[fieldName].amount
    });
  };

  const saveEditStandardField = () => {
    if (isNaN(Number(editStandardFieldData.amount))) {
      initAlert("Please enter a valid amount", "warning");
      return;
    }

         setCampaignFee({
       ...CampaignFee,
       [editingStandardField]: {
         name: CampaignFee[editingStandardField].name, // Keep original field name
         description: CampaignFee[editingStandardField].description, // Keep original description
         amount: Number(editStandardFieldData.amount)
       }
     });
    setEditingStandardField(null);
    setEditStandardFieldData({ name: "", description: "", amount: 0 });
    setGValue(true);
  };

  const cancelEditStandardField = () => {
    setEditingStandardField(null);
    setEditStandardFieldData({ name: "", description: "", amount: 0 });
  };

  const setImgFunction = async (name, files) => {
    try {
      setloader(true);
      if (name === "coverImg") {
        if (ProfileAssets.coverImage) {
          initAlert("Please delete the cover image", "warning");
          return;
        }
        let imgUrl = await handleUpload(files[0], process.env.REACT_APP_S3_COVER_PHOTO_FOLDER);
        // setCoverImg(imgUrl);
        setProfileAssets((prev) => ({ ...prev, coverImage: imgUrl }));
        setloader(false);
      } else if (name === "logo") {
        if (ProfileAssets.logo) {
          initAlert("Please delete the logo", "warning");
          return;
        }
        let imgUrl = await handleUpload(files[0], process.env.REACT_APP_S3_LOGO_PHOTO_FOLDER);
        // setLogo(imgUrl);
        setProfileAssets((prev) => ({ ...prev, logo: imgUrl }));
        setloader(false);
      } else if (name === "additionalImgs") {
        let newFiles = [];
        for (let obj of files) {
          let imgUrl = await handleUpload(obj, process.env.REACT_APP_S3_ADDITIONAL_PHOTO_FOLDER);
          newFiles.push(imgUrl);
        }
        // setAdditionalImgs((prevFiles) => [...prevFiles, ...newFiles]);
        setProfileAssets((prev) => ({
          ...prev,
          additionalPhotos: [...prev?.additionalPhotos, ...newFiles],
        }));
        setloader(false);
      }
    } catch (error) {
      initAlert("Upload failed", "error");
      setloader(false);
    }
  };

  const deleteImg = async (name, i) => {
    console.log(name);
    try {
      setloader(true);
      if (name === "coverImg") {
        console.log(ProfileAssets.coverImage);
        console.log(process.env.REACT_APP_S3_COVER_PHOTO_FOLDER);
        await handleDelete(ProfileAssets.coverImage, process.env.REACT_APP_S3_COVER_PHOTO_FOLDER);
        // setCoverImg("");
        setProfileAssets((prev) => ({ ...prev, coverImage: "" }));
        setloader(false);
      }
      if (name === "logo") {
        await handleDelete(ProfileAssets.logo, process.env.REACT_APP_S3_LOGO_PHOTO_FOLDER);
        setProfileAssets((prev) => ({ ...prev, logo: "" }));
        setloader(false);
      }
      if (name === "additionalImgs") {
        let oldArr = [...ProfileAssets?.additionalPhotos];
        await handleDelete(oldArr[i], process.env.REACT_APP_S3_ADDITIONAL_PHOTO_FOLDER);
        oldArr.splice(i, 1);
        setProfileAssets((prev) => ({ additionalPhotos: [...oldArr] }));
        setloader(false);
      }
    } catch (err) {
      initAlert("Delete failed", "error");
      setloader(false);
    }
  };

  const saveBtn = () => {
    setloader(true);

    // Create the new payload structure
    let payload = {
      id: location.state.id,
      title: CampaignGoals.title || CampaignGoals.toFunds || "Campaign Title",
      category: CampaignGoals.category,
      subCategory: CampaignGoals.subCategory,
      story: CampaignGoals.story,
      blog: CampaignGoals.blog,
      cause: CampaignGoals.fundsFor || "required", // Using fundsFor as cause
      goals: [
        {
          toFunds: CampaignGoals.toFunds,
          itemCount: Number(CampaignGoals.itemCount) || 0,
          donatingCount: Number(CampaignGoals.donatingCount) || 0,
          approxCost: Number(CampaignGoals.approxCost) || 0,
          imageUrl: "",
          productUrl: ""
        }
      ],
      location: {
        country: CampaignGoals.country,
        city: CampaignGoals.city,
        zipCode: CampaignGoals.zipCode,
        state: CampaignGoals.state
      },
      designAssets: {
        coverImage: ProfileAssets.coverImage,
        youtubeLink: ProfileAssets.youtubeLink,
        story: ProfileAssets.story
      },
                campaignFee: {
                    shippingAndDelivery: {
          name: "shippingAndDelivery",
          amount: Number(CampaignFee.shippingAndDelivery.amount) || 0,
          description: CampaignFee.shippingAndDelivery.description || ""
        },
        fulfillmentSupport: {
          name: "fulfillmentSupport",
          amount: Number(CampaignFee.fulfillmentSupport.amount) || 0,
          description: CampaignFee.fulfillmentSupport.description || ""
        },
        taxesAndProcessingFees: {
          name: "taxesAndProcessingFees",
          amount: Number(CampaignFee.taxesAndProcessingFees.amount) || 0,
          description: CampaignFee.taxesAndProcessingFees.description || ""
        },
        raflPlatformSupport: {
          name: "raflPlatformSupport",
          amount: Number(CampaignFee.raflPlatformSupport.amount) || 0,
          description: CampaignFee.raflPlatformSupport.description || ""
        },
        emergencyBuffer: {
          name: "emergencyBuffer",
          amount: Number(CampaignFee.emergencyBuffer.amount) || 0,
          description: CampaignFee.emergencyBuffer.description || ""
        },
        // Include custom fields as individual properties
        ...customFields.reduce((acc, field) => {
          acc[field.name] = {
            name: field.name,
            description: field.description || "",
            amount: Number(field.value) || 0
          };
          return acc;
        }, {})
      },
      campaignStatus: "Submitted", // Default status
      isSubmitted: true,
      isCampaignStop: false
    };

    updateCampaignDetails(payload)
      .then((res) => {
        setGValue(false);
        initAlert(res?.message);
        let payload = {
          id: location.state.id,
        };
        getCampaignDataById(payload);
      })
      .catch((err) => {
        setloader(false);
        if (err.response.data.error === "jwt expired") {
          initAlert("Your session is expired", "info");
          navigate("/");
          return;
        }
        initAlert(err?.response?.data?.message ?? "Error", "error");
      });
  };
  const changeCampFee = (event) => {
    let { name, value } = event.target;
    if (["shippingAndDelivery", "fulfillmentSupport", "taxesAndProcessingFees", "raflPlatformSupport", "emergencyBuffer"].includes(name)) {
      if (isNaN(Number(value))) {
        value = CampaignFee[name]?.amount || 0;
      }
      value = Number(value);

      setCampaignFee({
        ...CampaignFee,
        [name]: {
          ...CampaignFee[name], // Preserve existing field data including description
          amount: value
        },
      });
    } else {
      setCampaignFee({
        ...CampaignFee,
        [name]: value,
      });
    }
    setGValue(true);
  };

  const changeCampGoals = (event) => {
    let { name, value } = event.target;
    setCampaignGoals({ ...CampaignGoals, [name]: value });
  };

  useEffect(() => {
    // setloader(true);
    if (CampaignGoals.category !== "") {
      let subCategories = categList.find((data) => data.categoryName === CampaignGoals.category);
      let subCategValue = subCategories?.subCategory;
      setSubCategList(subCategValue);
      // setloader(false);
    }
  }, [CampaignGoals.category]);

  useEffect(() => {
    setloader(true);

    getAllCategoryData()
      .then((res) => {
        let categories = [...res?.sortedData];
        setCategList([...categories]);
        // initAlert(res?.message);
        setloader(false);
      })
      .catch((err) => {
        setloader(false);
        if (err.response.data.error === "jwt expired") {
          initAlert("Your session is expired", "info");
          navigate("/");
          return;
        }
        initAlert(err?.response?.data?.message ?? "Error", "error");
        console.error("error", err);
      });
  }, []);

  useEffect(() => {
    // Map the API response data to component state
    if (campaignFee) {
      // Handle the new API response structure with name and amount properties
      const mappedCampaignFee = {};
      const customFieldsFromProps = [];
      
      const fieldMappings = {
        'shippingAndDelivery': 'shippingAndDelivery',
        'deliveryCharges': 'fulfillmentSupport', 
        'fulfilmentShipping': 'fulfillmentSupport',
        'Taxes and Processing Fee': 'taxesAndProcessingFees',
        'RAFLFee': 'raflPlatformSupport',
        'afterProcessFee': 'taxesAndProcessingFees',
        'tax': 'taxesAndProcessingFees',
        'goalValue': 'shippingAndDelivery'
      };

      Object.keys(campaignFee).forEach(key => {
        if (campaignFee[key] && typeof campaignFee[key] === 'object' && campaignFee[key].name && typeof campaignFee[key].amount !== 'undefined') {
                     const newKey = fieldMappings[key] || key;
           const standardFields = ['shippingAndDelivery', 'fulfillmentSupport', 'taxesAndProcessingFees', 'raflPlatformSupport', 'emergencyBuffer'];
          
          if (standardFields.includes(newKey)) {
            mappedCampaignFee[newKey] = {
              name: newKey,
              amount: campaignFee[key].amount,
              description: campaignFee[key].description || ""
            };
          } else {
            customFieldsFromProps.push({
              id: Date.now() + Math.random(), // Generate unique ID
              name: campaignFee[key].name,
              description: campaignFee[key].description || "", // Include description if available
              value: campaignFee[key].amount.toString()
            });
          }
        } else if (typeof campaignFee[key] === 'number') {
          // Handle moneyNeeded field separately - don't map it to any standard field
          if (key === 'moneyNeeded') {
            // Keep moneyNeeded as is in the campaignFee prop - it will be used directly in total calculation
            return;
          }
          
          const newKey = fieldMappings[key];
          
          if (newKey) {
            const standardFields = ['shippingAndDelivery', 'fulfillmentSupport', 'taxesAndProcessingFees', 'raflPlatformSupport', 'emergencyBuffer'];
            if (standardFields.includes(newKey)) {
              mappedCampaignFee[newKey] = {
                name: newKey,
                amount: campaignFee[key],
                description: ""
              };
            }
          } else {
            customFieldsFromProps.push({
              id: Date.now() + Math.random(), // Generate unique ID
              name: key,
              description: "", // Default empty description for legacy fields
              value: campaignFee[key].toString()
            });
          }
        }
      });

      setCampaignFee({
        ...CampaignFee,
        ...mappedCampaignFee,
      });

      if (customFieldsFromProps.length > 0) {
        setCustomFields(customFieldsFromProps);
      }
    }

    if (campaignGoals) {
      // Handle the nested API response structure
      let mappedGoals = {
        ...CampaignGoals,
        // Map the nested structure
        title: campaignGoals.title || "",
        toFunds: campaignGoals.toFunds || "",
        category: campaignGoals.category || "",
        subCategory: campaignGoals.subCategory || "",
        story: campaignGoals.story || "",
        blog: campaignGoals.blog || "",
        fundsFor: campaignGoals.fundsFor || "",
        country: campaignGoals.country || "",
        city: campaignGoals.city || "",
        state: campaignGoals.state || "",
        zipCode: campaignGoals.zipCode || "",
        userDetails: campaignGoals.userDetails || [],
        goals: campaignGoals.goals || [],
      };

      // If there are goals in the array, use the first one to populate the form fields
      if (campaignGoals.goals && campaignGoals.goals.length > 0) {
        const firstGoal = campaignGoals.goals[0];
        mappedGoals = {
          ...mappedGoals,
          toFunds: firstGoal.toFunds || mappedGoals.toFunds,
          itemCount: firstGoal.itemCount || "",
          fundsFor: firstGoal.fundsFor || mappedGoals.fundsFor,
          donatingCount: firstGoal.donatingCount || "",
          approxCost: firstGoal.approxCost || "",
        };
      }

      setCampaignGoals(mappedGoals);
    }

    if (profileAssets) {
      // Handle the nested ProfileAssets structure
      setProfileAssets({
        ...ProfileAssets,
        coverImage: profileAssets.coverImage || "",
        youtubeLink: profileAssets.youtubeLink || "",
        additionalPhotos: profileAssets.additionalPhotos || [],
        story: profileAssets.story || "",
      });
    }
  }, [campaignFee, campaignGoals, profileAssets]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header Section */}
      <div className="mb-8">
        <Heading size="6" className="text-gray-900 font-semibold mb-2">
          Campaign Details
        </Heading>
        <Text size="3" className="text-gray-600">
          Manage your campaign information and assets
        </Text>
      </div>

      {/* Campaign Fee Section */}
      <Card className="p-6 bg-white shadow-sm border border-gray-200">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-blue-500 rounded-full mr-4"></div>
          <Heading size="4" className="text-gray-900 font-semibold">
            Campaign Fee Structure
          </Heading>
        </div>

        <div className="space-y-4 mb-6">
          {/* Standard Fee Fields */}
          {Object.entries(CampaignFee).map(([fieldKey, fieldData]) => {
            // Create readable display names
            const getDisplayName = (key) => {
              const displayNames = {
                shippingAndDelivery: "Shipping & Delivery",
                fulfillmentSupport: "Fulfillment Support",
                taxesAndProcessingFees: "Taxes & Processing Fees",
                raflPlatformSupport: "RAFL Platform Support",
                emergencyBuffer: "Emergency Buffer"
              };
              return displayNames[key] || key;
            };

            return (
              <Card key={fieldKey} className="p-4 border border-gray-200 hover:border-blue-200 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Text size="3" className="text-gray-900 font-semibold">
                        {getDisplayName(fieldKey)}
                      </Text>
                      <Badge variant="soft" color="blue" size="1">
                        Fixed Amount
                      </Badge>
                    </div>

                    {fieldData.description ? (
                      <Text size="2" className="text-gray-600 mb-3 leading-relaxed">
                        {fieldData.description}
                      </Text>
                    ) : (
                      <Text size="2" className="text-gray-400 mb-3 italic">
                        No description provided
                      </Text>
                    )}

                    <div className="flex items-center gap-2">
                      <Text size="2" className="text-gray-500 min-w-0">
                        Amount:
                      </Text>
                      <TextField.Root
                        value={fieldData.amount}
                        onChange={(e) => changeCampFee(e)}
                        name={fieldKey}
                        variant="soft"
                        size="2"
                        className="w-28"
                        placeholder="0"
                      >
                        <TextField.Slot className="text-gray-500">
                          $
                        </TextField.Slot>
                      </TextField.Root>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      size="2"
                      variant="soft"
                      color="blue"
                      onClick={() => startEditingStandardField(fieldKey)}
                      className="whitespace-nowrap"
                    >
                      Edit Field
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Custom Fields Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Text size="3" className="text-gray-900 font-semibold">
                Custom Fee Fields
              </Text>
              <Text size="2" className="text-gray-500">
                Add additional fees specific to your campaign
              </Text>
            </div>
            <Button
              size="3"
              onClick={() => setShowCustomFieldModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              + Add Custom Field
            </Button>
          </div>

          {customFields.length > 0 ? (
            <div className="space-y-4">
              {customFields.map((field) => (
                <Card key={field.id} className="p-4 border border-gray-200 hover:border-green-200 transition-colors">
                  {editingField === field.id ? (
                    // Edit mode
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Text size="2" className="text-gray-700 font-medium">
                          Editing Custom Field
                        </Text>
                        <Badge variant="soft" color="orange" size="1">
                          Edit Mode
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Text size="2" className="text-gray-700 font-medium">
                            Field Name
                          </Text>
                          <TextField.Root
                            value={editFieldData.name}
                            onChange={(e) => setEditFieldData({ ...editFieldData, name: e.target.value })}
                            variant="soft"
                            size="2"
                            className="w-full"
                            placeholder="Enter field name"
                          />
                        </div>

                        <div className="space-y-2">
                          <Text size="2" className="text-gray-700 font-medium">
                            Amount ($)
                          </Text>
                          <TextField.Root
                            value={editFieldData.value}
                            onChange={(e) => setEditFieldData({ ...editFieldData, value: e.target.value })}
                            variant="soft"
                            size="2"
                            className="w-full"
                            placeholder="Enter amount"
                          >
                            <TextField.Slot className="text-gray-500">$</TextField.Slot>
                          </TextField.Root>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Text size="2" className="text-gray-700 font-medium">
                          Description (Optional)
                        </Text>
                        <TextArea
                          value={editFieldData.description}
                          onChange={(e) => setEditFieldData({ ...editFieldData, description: e.target.value })}
                          variant="soft"
                          size="2"
                          className="w-full"
                          placeholder="Enter field description"
                        />
                      </div>

                      <div className="flex justify-end gap-3">
                        <Button
                          size="2"
                          variant="soft"
                          onClick={cancelEditField}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="2"
                          onClick={saveEditField}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Text size="3" className="text-gray-900 font-semibold">
                            {field.name}
                          </Text>
                          <Badge variant="soft" color="green" size="1">
                            Custom Field
                          </Badge>
                        </div>

                        {field.description ? (
                          <Text size="2" className="text-gray-600 mb-3 leading-relaxed">
                            {field.description}
                          </Text>
                        ) : (
                          <Text size="2" className="text-gray-400 mb-3 italic">
                            No description provided
                          </Text>
                        )}

                        <div className="flex items-center gap-2">
                          <Text size="2" className="text-gray-500">
                            Amount:
                          </Text>
                          <TextField.Root
                            value={field.value}
                            onChange={(e) => updateCustomField(field.id, 'value', e.target.value)}
                            variant="soft"
                            size="2"
                            className="w-28"
                            placeholder="0"
                          >
                            <TextField.Slot className="text-gray-500">$</TextField.Slot>
                          </TextField.Root>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          size="2"
                          variant="soft"
                          color="blue"
                          onClick={() => startEditingField(field)}
                          className="whitespace-nowrap"
                        >
                          Edit Field
                        </Button>
                        <Button
                          size="2"
                          variant="soft"
                          color="red"
                          onClick={() => removeCustomField(field.id)}
                          className="whitespace-nowrap"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 border border-dashed border-gray-300">
              <div className="text-center">
                <Text size="2" className="text-gray-500 mb-3 block">
                  No custom fields added yet
                </Text>
                <Text size="1" className="text-gray-400">
                  Click "Add Custom Field" to create additional fee categories
                </Text>
              </div>
            </Card>
          )}
        </div>

        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Text size="4" className="text-gray-900 font-bold">
                Total Campaign Goal
              </Text>
              <Badge variant="soft" color="blue" size="2">
                Auto-Calculated
              </Badge>
            </div>

            <div className="mb-4">
              <Text size="6" className="text-blue-700 font-bold">
                ${totalGoalValue}
              </Text>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  Money Needed
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${campaignFee?.moneyNeeded || 0}
                </Text>
              </div>
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  Shipping & Delivery
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${CampaignFee.shippingAndDelivery.amount}
                </Text>
              </div>
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  Fulfillment Support
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${CampaignFee.fulfillmentSupport.amount}
                </Text>
              </div>
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  Taxes & Processing
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${CampaignFee.taxesAndProcessingFees.amount}
                </Text>
              </div>
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  RAFL Platform
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${CampaignFee.raflPlatformSupport.amount}
                </Text>
              </div>
              <div>
                <Text size="1" className="text-gray-500 uppercase tracking-wide">
                  Emergency Buffer
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${CampaignFee.emergencyBuffer.amount}
                </Text>
              </div>
            </div>

            {customFields.length > 0 && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <Text size="1" className="text-gray-500 uppercase tracking-wide mb-2">
                  Custom Fields Total
                </Text>
                <Text size="2" className="text-gray-700 font-semibold ml-2">
                  ${customFields.reduce((total, field) => total + (Number(field.value) || 0), 0)}
                </Text>
              </div>
            )}
          </div>
        </Card>
      </Card>

      {/* Campaign Goals Section */}
      <Card className="p-6 bg-white shadow-sm border border-gray-200">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-green-500 rounded-full mr-4"></div>
          <Heading size="4" className="text-gray-900 font-semibold">
            Campaign Goals
          </Heading>
        </div>

        <div className="space-y-6">
          {/* Campaign Title */}
          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Campaign Title
            </Text>
            <TextField.Root
              value={CampaignGoals.title || CampaignGoals.toFunds}
              onChange={(e) => changeCampGoals(e)}
              name="title"
              variant="soft"
              className="w-full"
              placeholder="Enter campaign title"
            />
          </div>

          <Grid columns="2" gap="4">
            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                To Fund
              </Text>
              <TextField.Root
                value={CampaignGoals.toFunds}
                onChange={(e) => changeCampGoals(e)}
                name="toFunds"
                variant="soft"
                className="w-full"
                placeholder="What are you funding?"
              />
            </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                For
              </Text>
              <TextField.Root
                value={CampaignGoals.fundsFor}
                onChange={(e) => changeCampGoals(e)}
                name="fundsFor"
                variant="soft"
                className="w-full"
                placeholder="Who is this for?"
              />
            </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Quantity (Optional)
              </Text>
              <TextField.Root
                value={CampaignGoals.itemCount}
                onChange={(e) => changeCampGoals(e)}
                name="itemCount"
                variant="soft"
                className="w-full"
                placeholder="How many items?"
              />
            </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Approximate Cost
              </Text>
              <TextField.Root
                value={CampaignGoals.approxCost}
                onChange={(e) => changeCampGoals(e)}
                name="approxCost"
                variant="soft"
                className="w-full"
                placeholder="Estimated cost"
              >
                <TextField.Slot className="text-gray-500">$</TextField.Slot>
              </TextField.Root>
            </div>
          </Grid>

          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Your Story
            </Text>
            <TextArea
              size="3"
              value={CampaignGoals.story}
              onChange={(e) => changeCampGoals(e)}
              name="story"
              variant="soft"
              placeholder="Tell your compelling story..."
              className="min-h-[120px]"
            />
          </div>

          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Blog Content
            </Text>
            <TextArea
              size="3"
              value={CampaignGoals.blog}
              onChange={(e) => changeCampGoals(e)}
              name="blog"
              variant="soft"
              placeholder="Additional blog content..."
              className="min-h-[120px]"
            />
          </div>
        </div>
      </Card>

      {/* Location Information */}
      <Card className="p-6 bg-white shadow-sm border border-gray-200">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-purple-500 rounded-full mr-4"></div>
          <Heading size="4" className="text-gray-900 font-semibold">
            Location Details
          </Heading>
        </div>

        <Grid columns="2" gap="4">
          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Country
            </Text>
            <TextField.Root
              value={CampaignGoals.country}
              onChange={(e) => changeCampGoals(e)}
              name="country"
              size="3"
              variant="soft"
              className="w-full"
              placeholder="Enter country"
            />
          </div>

          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              State
            </Text>
            <TextField.Root
              value={CampaignGoals.state}
              onChange={(e) => changeCampGoals(e)}
              name="state"
              size="3"
              variant="soft"
              className="w-full"
              placeholder="Enter state"
            />
          </div>

          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              City
            </Text>
            <TextField.Root
              value={CampaignGoals.city}
              onChange={(e) => changeCampGoals(e)}
              name="city"
              size="3"
              variant="soft"
              className="w-full"
              placeholder="Enter city"
            />
          </div>

          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Zip Code
            </Text>
            <TextField.Root
              value={CampaignGoals.zipCode}
              onChange={(e) => changeCampGoals(e)}
              name="zipCode"
              size="3"
              variant="soft"
              className="w-full"
              placeholder="Enter zip code"
            />
          </div>
        </Grid>
      </Card>

      {/* Category Selection */}
      {categList.length !== 0 && (
        <Card className="p-6 bg-white shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <div className="w-1 h-8 bg-orange-500 rounded-full mr-4"></div>
            <Heading size="4" className="text-gray-900 font-semibold">
              Category Information
            </Heading>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Category
              </Text>
              <Select.Root
                value={CampaignGoals.category}
                onValueChange={(e) => setCampaignGoals({ ...CampaignGoals, category: e })}
                size="3"
              >
                <Select.Trigger variant="soft" className="w-full" />
                <Select.Content>
                  {categList.map((data, i) => (
                    <Select.Item key={i} value={data?.categoryName}>
                      {data?.categoryName}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>

            {subCategList && (
              <div className="space-y-2">
                <Text size="2" className="text-gray-700 font-medium">
                  Sub Category
                </Text>
                <Select.Root
                  value={CampaignGoals.subCategory}
                  onValueChange={(e) => setCampaignGoals({ ...CampaignGoals, subCategory: e })}
                  size="3"
                >
                  <Select.Trigger variant="soft" className="w-full" />
                  <Select.Content>
                    {subCategList.map((data, i) => (
                      <Select.Item key={i} value={data}>
                        {data}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </div>
            )}

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                User Details
              </Text>
              <TextField.Root
                size="3"
                variant="soft"
                readOnly
                className="w-full"
                value={
                  CampaignGoals?.userDetails?.[0]
                    ? `${CampaignGoals.userDetails[0].firstName || ""} ${CampaignGoals.userDetails[0].lastName || ""}`.trim()
                    : ""
                }
                placeholder="User information will appear here"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Profile Assets Section */}
      <Card className="p-6 bg-white shadow-sm border border-gray-200">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-indigo-500 rounded-full mr-4"></div>
          <Heading size="4" className="text-gray-900 font-semibold">
            Profile Assets
          </Heading>
        </div>

        <div className="space-y-8">
          {/* Cover Image */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text size="2" className="text-gray-700 font-medium">
                Cover Image
              </Text>
              <Badge variant="soft" color="gray" size="1">
                Required
              </Badge>
            </div>
            <Flex gap="4" align="start">
              <DropZone name={"coverImg"} func={setImgFunction} />
              {ProfileAssets.coverImage && (
                <div className="relative group">
                  <Avatar
                    className="absolute -top-2 -right-2 z-10 bg-red-500 hover:bg-red-600 cursor-pointer transition-colors"
                    src={trashIcon}
                    fallback="X"
                    size="1"
                    radius="large"
                    onClick={() => deleteImg("coverImg")}
                  />
                  <img
                    alt="cover"
                    className="w-44 h-32 object-cover rounded-lg border border-gray-200"
                    src={ProfileAssets.coverImage}
                  />
                </div>
              )}
            </Flex>
          </div>

          <Separator size="4" />

          {/* Logo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text size="2" className="text-gray-700 font-medium">
                Logo
              </Text>
              <Badge variant="soft" color="gray" size="1">
                Required
              </Badge>
            </div>
            <Flex gap="4" align="start">
              <DropZone name={"logo"} func={setImgFunction} />
              {ProfileAssets.logo && (
                <div className="relative group">
                  <Avatar
                    className="absolute -top-2 -right-2 z-10 bg-red-500 hover:bg-red-600 cursor-pointer transition-colors"
                    fallback="X"
                    size="1"
                    src={trashIcon}
                    radius="large"
                    onClick={() => deleteImg("logo")}
                  />
                  <img
                    alt="logo"
                    className="w-44 h-32 object-cover rounded-lg border border-gray-200"
                    src={ProfileAssets.logo}
                  />
                </div>
              )}
            </Flex>
          </div>

          <Separator size="4" />

          {/* Additional Photos */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text size="2" className="text-gray-700 font-medium">
                Additional Photos
              </Text>
              <Badge variant="soft" color="blue" size="1">
                Optional
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <DropZone name={"additionalImgs"} func={setImgFunction} />
              {ProfileAssets?.additionalPhotos?.map((data, i) => (
                <div key={i} className="relative group">
                  <Avatar
                    className="absolute -top-2 -right-2 z-10 bg-red-500 hover:bg-red-600 cursor-pointer transition-colors"
                    fallback="X"
                    size="1"
                    radius="large"
                    src={trashIcon}
                    onClick={() => deleteImg("additionalImgs", i)}
                  />
                  <img
                    alt="additional"
                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    src={data}
                  />
                </div>
              ))}
            </div>
          </div>

          <Separator size="4" />

          {/* YouTube Link */}
          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              YouTube Link
            </Text>
            <TextField.Root
              value={ProfileAssets.youtubeLink}
              size="3"
              variant="soft"
              className="w-full"
              placeholder="Enter YouTube video URL"
              onChange={(e) =>
                setProfileAssets((prev) => ({
                  ...prev,
                  youtubeLink: e.target.value,
                }))
              }
            />
          </div>

          {/* Profile Story */}
          <div className="space-y-2">
            <Text size="2" className="text-gray-700 font-medium">
              Profile Story
            </Text>
            <TextArea
              size="3"
              value={ProfileAssets.story}
              onChange={(e) =>
                setProfileAssets((prev) => ({
                  ...prev,
                  story: e.target.value,
                }))
              }
              variant="soft"
              className="min-h-[120px]"
              placeholder="Tell your profile story..."
            />
          </div>

          {ProfileAssets.youtubeLink && <YoutubeThumbnail youtubeLink={ProfileAssets.youtubeLink} />}
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <Button
          size="3"
          onClick={() => saveBtn()}
          className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm"
        >
          Save Campaign Details
        </Button>
      </div>

      {/* Custom Field Modal */}
      <Dialog.Root open={showCustomFieldModal} onOpenChange={setShowCustomFieldModal}>
        <Dialog.Content className="w-96">
          <Dialog.Title className="text-lg font-semibold mb-4">
            Add Custom Field
          </Dialog.Title>

          <div className="space-y-4">
            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Field Name
              </Text>
              <TextField.Root
                value={newCustomField.name}
                onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                variant="soft"
                className="w-full"
                placeholder="Enter field name"
              />
            </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Description (Optional)
              </Text>
              <TextArea
                value={newCustomField.description}
                onChange={(e) => setNewCustomField({ ...newCustomField, description: e.target.value })}
                variant="soft"
                className="w-full"
                placeholder="Enter field description"
                size="2"
              />
            </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Value ($)
              </Text>
              <TextField.Root
                value={newCustomField.value}
                onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
                variant="soft"
                className="w-full"
                placeholder="Enter amount"
              >
                <TextField.Slot className="text-gray-500">$</TextField.Slot>
              </TextField.Root>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="soft"
              onClick={() => setShowCustomFieldModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={addCustomField}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Field
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {/* Standard Field Edit Modal */}
      <Dialog.Root open={editingStandardField !== null} onOpenChange={() => setEditingStandardField(null)}>
        <Dialog.Content className="w-96">
          <Dialog.Title className="text-lg font-semibold mb-4">
            Edit Field: {editStandardFieldData.name}
          </Dialog.Title>

          <div className="space-y-4">
            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Field Name
              </Text>
              <div className="p-3 bg-gray-50 rounded-md border flex justify-between">
                <Text size="2" className="text-gray-700 font-medium mb-2">
                  {editStandardFieldData.name}
                </Text>
                <Text size="1" className="text-gray-500">
                  Standard field names cannot be changed
                </Text>
              </div>
            </div>

                         <div className="space-y-2">
               <Text size="2" className="text-gray-700 font-medium">
                 Description
               </Text>
               <div className="p-3 bg-gray-50 rounded-md border">
                 <Text size="2" className="text-gray-700">
                   {editStandardFieldData.description}
                 </Text>
                 <Text size="1" className="text-gray-500 mt-1">
                   Standard field descriptions cannot be changed
                 </Text>
               </div>
             </div>

            <div className="space-y-2">
              <Text size="2" className="text-gray-700 font-medium">
                Amount ($)
              </Text>
              <TextField.Root
                value={editStandardFieldData.amount}
                onChange={(e) => setEditStandardFieldData({ ...editStandardFieldData, amount: e.target.value })}
                variant="soft"
                className="w-full"
                placeholder="Enter amount"
              >
                <TextField.Slot className="text-gray-500">
                  $
                </TextField.Slot>
              </TextField.Root>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="soft"
              onClick={cancelEditStandardField}
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditStandardField}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
};

export default CampaignDetails;
